import React, { useState } from "react"
import {
	Typography,
	Button,
	Card,
	Row,
	Col,
	Upload,
	List,
	Space,
	Divider,
	message,
} from "antd"
import { UploadOutlined, DeleteOutlined, FileOutlined, CopyOutlined } from "@ant-design/icons"
import { MD5 } from "crypto-js"
import "./style.css"

const { Title, Text } = Typography

const MD5Hasher = () => {
	const [fileHashes, setFileHashes] = useState([])

	const handleFileSelect = (file) => {
		const reader = new FileReader()
		
		reader.onload = (e) => {
			const arrayBuffer = e.target.result
			const wordArray = MD5(arrayBufferToWordArray(arrayBuffer))
			const hash = wordArray.toString()
			
			setFileHashes(prev => [...prev, {
				name: file.name,
				hash: hash,
				size: file.size,
				type: file.type,
				key: `${file.name}-${Date.now()}`
			}])
		}
		
		reader.readAsArrayBuffer(file)
		return false // Prevent automatic upload
	}

	const arrayBufferToWordArray = (arrayBuffer) => {
		const uint8Array = new Uint8Array(arrayBuffer)
		const words = []
		for (let i = 0; i < uint8Array.length; i++) {
			words[i >>> 2] |= uint8Array[i] << (24 - (i % 4) * 8)
		}
		return { words: words, sigBytes: uint8Array.length }
	}

	const handleReset = () => {
		setFileHashes([])
	}

	const handleCopyAll = () => {
		const hashText = fileHashes.map(file => `${file.name}, ${file.hash}`).join('\n')
		navigator.clipboard.writeText(hashText).then(() => {
			message.success(`Copied ${fileHashes.length} hash${fileHashes.length > 1 ? 'es' : ''} to clipboard`)
		}).catch(() => {
			message.error('Failed to copy to clipboard')
		})
	}

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
	}

	return (
		<div className="md5-hasher-container">
			<div className="header-section">
				<Title level={2}>MD5 Hasher</Title>
				<Text type="secondary">
					Upload multiple files to calculate their MD5 hashes
				</Text>
			</div>

			<Row gutter={24}>
				<Col xs={24} md={10}>
					<Card 
						title="File Upload" 
						className="upload-card"
						extra={
							fileHashes.length > 0 && (
								<Button 
									type="primary" 
									danger 
									icon={<DeleteOutlined />}
									onClick={handleReset}
								>
									Reset
								</Button>
							)
						}
					>
						<Upload.Dragger
							multiple
							beforeUpload={handleFileSelect}
							showUploadList={false}
							className="file-uploader"
						>
							<p className="ant-upload-drag-icon">
								<UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
							</p>
							<p className="ant-upload-text">Click or drag files to this area to upload</p>
							<p className="ant-upload-hint">
								Support for multiple file selection. Files are processed locally in your browser.
							</p>
						</Upload.Dragger>

						<Divider />

						<Space direction="vertical" style={{ width: '100%' }}>
							<Text strong>Files Selected: {fileHashes.length}</Text>
							{fileHashes.length > 0 && (
								<Text type="secondary" style={{ fontSize: '12px' }}>
									Scroll right panel to view all hashes
								</Text>
							)}
						</Space>
					</Card>
				</Col>

				<Col xs={24} md={14}>
					<Card 
						title="File Hashes" 
						className="results-card"
						extra={
							fileHashes.length > 0 && (
								<Button 
									type="default" 
									icon={<CopyOutlined />}
									onClick={handleCopyAll}
								>
									Copy All Hashes
								</Button>
							)
						}
					>
						{fileHashes.length === 0 ? (
							<div className="empty-state">
								<FileOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
								<Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
									No files uploaded yet. Select files to see their MD5 hashes.
								</Text>
							</div>
						) : (
							<List
								dataSource={fileHashes}
								renderItem={(item) => (
									<List.Item key={item.key} className="hash-list-item">
										<List.Item.Meta
											avatar={<FileOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
											title={
												<Space direction="vertical" size={4} style={{ width: '100%' }}>
													<Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
													<Text type="secondary" style={{ fontSize: '12px' }}>
														{formatFileSize(item.size)} {item.type && `• ${item.type}`}
													</Text>
												</Space>
											}
											description={
												<div className="hash-display">
													<Text code copyable style={{ fontSize: '12px', wordBreak: 'break-all' }}>
														{item.hash}
													</Text>
												</div>
											}
										/>
									</List.Item>
								)}
								className="hash-list"
							/>
						)}
					</Card>
				</Col>
			</Row>
		</div>
	)
}

export default MD5Hasher
